import React from "react";
import { NavLink, Outlet } from "react-router-dom";
import { LayoutDashboard, Users, ClipboardList, ShoppingBag, MapPin, BarChart3, LogOut, Activity, Sheet, MessageCircle, Download, ShieldCheck, ScanLine, Receipt, MessageSquareWarning, Plug, UserCheck } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";

const nav = [
  { to: "/dashboard", label: "Overview", icon: LayoutDashboard, tid: "nav-dashboard" },
  { to: "/enrollments", label: "Enrollments", icon: ClipboardList, tid: "nav-enrollments" },
  { to: "/orders", label: "Orders", icon: ShoppingBag, tid: "nav-orders" },
  { to: "/chats", label: "WhatsApp Bot", icon: MessageCircle, tid: "nav-chats" },
  { to: "/members", label: "Members", icon: Users, tid: "nav-members" },
  { to: "/pins", label: "PIN Serviceability", icon: MapPin, tid: "nav-pins" },
  { to: "/prescription-ai", label: "Prescription AI", icon: ScanLine, tid: "nav-prescription-ai" },
  { to: "/billing", label: "Billing", icon: Receipt, tid: "nav-billing" },
  { to: "/complaints", label: "Complaints", icon: MessageSquareWarning, tid: "nav-complaints" },
  { to: "/verification/insured-bk", label: "Insured BK Roster", icon: UserCheck, tid: "nav-insured-bk" },
  { to: "/integrations", label: "Integrations", icon: Plug, tid: "nav-integrations" },
  { to: "/analytics", label: "Analytics & BI", icon: BarChart3, tid: "nav-analytics" },
];

export default function DashboardLayout() {
  const { user, logout } = useAuth();
  const navItems = user?.is_owner
    ? [...nav, { to: "/admin/allowlist", label: "Admin Access", icon: ShieldCheck, tid: "nav-admin-allowlist" }]
    : nav;

  return (
    <div className="min-h-screen flex bg-background">
      {/* Sidebar */}
      <aside className="w-64 shrink-0 bg-primary text-primary-foreground flex flex-col">
        <div className="px-6 py-6 border-b border-white/10 flex items-center gap-3">
          <div className="h-9 w-9 rounded-xl bg-accent flex items-center justify-center">
            <Activity size={18} className="text-white" />
          </div>
          <div>
            <div className="font-heading font-bold text-lg leading-none">Medishiv</div>
            <div className="label-caps text-white/60 mt-1">Ops Console</div>
          </div>
        </div>
        <nav className="flex-1 p-3 space-y-1">
          {navItems.map(({ to, label, icon: Icon, tid }) => (
            <NavLink
              key={to}
              to={to}
              data-testid={tid}
              className={({ isActive }) =>
                `flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm transition-colors ${
                  isActive ? "bg-white/15 text-white" : "text-white/70 hover:bg-white/10 hover:text-white"
                }`
              }
            >
              <Icon size={18} />
              <span>{label}</span>
            </NavLink>
          ))}
        </nav>
        <div className="p-4 border-t border-white/10">
          <div className="flex items-center gap-3">
            <Avatar className="h-9 w-9 border border-white/20">
              <AvatarImage src={user?.picture} alt={user?.name} />
              <AvatarFallback className="bg-white/10 text-white text-xs">
                {(user?.name || user?.email || "U").slice(0, 2).toUpperCase()}
              </AvatarFallback>
            </Avatar>
            <div className="flex-1 min-w-0">
              <div className="text-sm font-medium truncate">{user?.name || "Staff"}</div>
              <div className="text-xs text-white/60 truncate">{user?.email}</div>
            </div>
            <Button
              data-testid="logout-btn"
              onClick={logout}
              size="icon"
              variant="ghost"
              className="text-white/70 hover:text-white hover:bg-white/10"
            >
              <LogOut size={16} />
            </Button>
          </div>
        </div>
      </aside>

      {/* Main */}
      <main className="flex-1 min-w-0 overflow-x-hidden">
        <Outlet />
      </main>
    </div>
  );
}
